import { useState, useEffect } from "react";
import { router, routes } from "../../app/routes";
import {
  Card,
  CardBody,
  SpinnerDefault,
  TextWithValidation,
} from "../../components";
import { CardTitle } from "../../components/card/CardTitle";
import { Form } from "react-bootstrap";
import { PageContent, PageBreadcrumbProps } from "../PageContent";
import SettingsSubmenu from "./SettingsSubmenu";
import SettingsModel from "../../data/entities/SettingsModel";
import SettingsService from "../../services/SettingsService";
import BlockButton from "../../components/form/buttons/BlockButton";
import { showSuccessMessage } from "../../app/tools";

export interface ScaleSettingsValidationModel {
  flwBaseline: boolean;
  flawBaseline: boolean;
  flawMultiplier: boolean;
  wall1Baseline: boolean;
  wall1Multiplier: boolean;
  wall2Baseline: boolean;
  wall2Multiplier: boolean;
}

export function SettingsScalePage() {
  const title: string = "Default Scale";
  const breadcrumbs: PageBreadcrumbProps = {
    showHome: true,
    values: [{ label: "Settings", url: routes.settings }, { label: "Scale" }],
  };

  const [pageData, setPageData] = useState<SettingsModel | undefined | null>(
    undefined
  );
  const [showSpinner, setShowSpinner] = useState(false);
  const [isFormValid, setIsFormValid] =
    useState<ScaleSettingsValidationModel>();

  const dataFields = [
    {
      id: "flawBaseline",
      label: "Flaw Baseline",
      initialValue: pageData?.scale.flawBaseline?.toString(),
    },
    {
      id: "flawMultiplier",
      label: "Flaw Multiplier",
      initialValue: pageData?.scale.flawMultiplier?.toString(),
    },
    {
      id: "wall1Baseline",
      label: "Wall1 Baseline",
      initialValue: pageData?.scale.wall1Baseline?.toString(),
    },
    {
      id: "wall1Multiplier",
      label: "Wall1 Multiplier",
      initialValue: pageData?.scale.wall1Multiplier?.toString(),
    },
    {
      id: "wall2Baseline",
      label: "Wall2 Baseline",
      initialValue: pageData?.scale.wall2Baseline?.toString(),
    },
    {
      id: "wall2Multiplier",
      label: "Wall2 Multiplier",
      initialValue: pageData?.scale.wall2Multiplier?.toString(),
    },
  ];

  /**
   * Fetches the page data when the component mounts.
   */
  useEffect(() => {
    setIsFormValid({
      flwBaseline: true,
      flawBaseline: true,
      flawMultiplier: true,
      wall1Baseline: true,
      wall1Multiplier: true,
      wall2Baseline: true,
      wall2Multiplier: true,
    });

    const fetchData = async () => {
      try {
        const loadedData = await new SettingsService().getAllAsync();
        setPageData(loadedData);
      } catch (e) {
        console.error("Error fetching settings: ", e);
      }
    };
    fetchData();
  }, []);

  /**
   * Handles changes to the input fields by updating the page data.
   * @param field - The name of the field to update.
   * @param value - The new value of the field.
   */
  const handleInputChange = (field: string, value: any) => {
    setPageData((prevPageData) => {
      if (!prevPageData) return null;
      const newPageData = { ...prevPageData } as SettingsModel;
      if (field in newPageData.scale) {
        (newPageData.scale as any)[field] = value;
      }
      return newPageData;
    });
  };

  /**
   * Handles the submission of the form by saving the updated site settings.
   */
  async function handleSaveSettings() {
    setShowSpinner(true);
    if (pageData) {
      const scale = pageData.scale;
      const ok: boolean | undefined =
        await new SettingsService().changeScaleAsync(scale);
      console.log(ok);
      if (!ok) {
        console.error("Error changing scale settings");
        return;
      }
    }
    setShowSpinner(false);
    showSuccessMessage("Scale settings updated.");
    router.navigate(routes.settingsScale);
  }

  /**
   * Checks if every field in the form is valid.
   * @returns boolean
   */
  function isButtonEnabled() {
    if (isFormValid) {
      const result = Object.values(isFormValid).every(
        (value) => value === true
      );
      return result;
    }
  }

  if (pageData === undefined) {
    return (
      <PageContent
        title={title}
        breadcrumbs={breadcrumbs}
        submenu={<SettingsSubmenu />}
      >
        <div className="position-relative">
          <SpinnerDefault />
        </div>
      </PageContent>
    );
  } else {
    return (
      <PageContent
        title={title}
        breadcrumbs={breadcrumbs}
        submenu={<SettingsSubmenu />}
      >
        <Card>
          <CardTitle>
            <h6 className="text-light m-0">
              Modify default scale for new inspections.
            </h6>
          </CardTitle>
          <CardBody>
            <Form>
              <div className="row mt-2">
                <div className="col-12 col-lg-6">
                  <TextWithValidation
                    id="flwBaseline"
                    type="number"
                    label="FLW Baseline"
                    required={true}
                    initialValue={pageData?.scale.flwBaseline?.toString()}
                    onChangeText={(value) => {
                      handleInputChange("flwBaseline", value);
                    }}
                    validationResult={(input, result) => {
                      setIsFormValid((current) => ({
                        ...current!,
                        flawBaseline: result,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="row">
                {dataFields.map((field) => (
                  <div key={field.label} className={`col col-12 col-lg-6`}>
                    <TextWithValidation
                      id={field.id}
                      label={field.label}
                      required={true}
                      type={"number"}
                      onChangeText={(value) => {
                        handleInputChange(field.id, value);
                      }}
                      initialValue={field.initialValue}
                      validationResult={(input, result) => {
                        setIsFormValid(() => {
                          return {
                            ...(isFormValid as ScaleSettingsValidationModel),
                            [field.id]: result,
                          };
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="row">
                <div className="col col-1">
                  <BlockButton
                    label="Save"
                    onClick={handleSaveSettings}
                    disabled={!isButtonEnabled()}
                    showSpinner={showSpinner}
                  />
                </div>
              </div>
            </Form>
          </CardBody>
        </Card>
      </PageContent>
    );
  }
}

export default SettingsScalePage;

